import { useEffect, useState, useCallback } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useOptions } from "@/context/OptionsContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, Users2, ListChecks, DollarSign, Plus, Pencil, Trash2, Tags, Loader2, UserPlus, Upload, Gift, BellRing, Bell, Inbox, Send, ArrowRight, GripVertical } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import * as XLSX from "xlsx";

const parseCSVLine = (line) => {
  const out = []; let cur = ""; let q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (q) {
      if (c === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else q = false; }
      else cur += c;
    } else if (c === '"') { q = true; }
    else if (c === ",") { out.push(cur); cur = ""; }
    else cur += c;
  }
  out.push(cur);
  return out;
};

const parseCSV = (text) => {
  const lines = (text || "").trim().split(/\r?\n/).filter((l) => l.length);
  if (lines.length < 2) return [];
  const headers = parseCSVLine(lines[0]).map((h) => h.trim().toLowerCase());
  return lines.slice(1).map((line) => {
    const cells = parseCSVLine(line);
    const row = {};
    headers.forEach((h, i) => { row[h] = (cells[i] || "").trim(); });
    if (row.premium_estimate) row.premium_estimate = parseFloat(row.premium_estimate) || null;
    return row;
  });
};

const Stat = ({ icon: Icon, label, value }) => (
  <Card className="p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 text-primary"><Icon className="h-5 w-5" /></div>
      <div><p className="text-xs text-muted-foreground">{label}</p><p className="font-heading text-xl font-bold">{value}</p></div>
    </div>
  </Card>
);

const TAXONOMY_META = [
  { name: "insurance_types", label: "Coverage / Insurance types" },
  { name: "lead_statuses", label: "Lead statuses" },
  { name: "lead_temperatures", label: "Lead temperatures" },
];

function OptionRow({ name, item, onSaved, onPersistOrder }) {
  const controls = useDragControls();
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(item.label);
  const [color, setColor] = useState(item.color);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try { await api.patch(`/superadmin/options/${name}/${item.key}`, { label, color }); toast.success("Option updated"); setEditing(false); onSaved(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${item.label}"? Existing leads keep their value.`)) return;
    try { await api.delete(`/superadmin/options/${name}/${item.key}`); toast.success("Option deleted"); onSaved(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Reorder.Item value={item} dragListener={false} dragControls={controls} onDragEnd={onPersistOrder}
      className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2" data-testid={`option-row-${name}-${item.key}`}>
      <button type="button" onPointerDown={(e) => controls.start(e)}
        className="shrink-0 cursor-grab touch-none text-muted-foreground transition-colors hover:text-foreground active:cursor-grabbing"
        title="Drag to reorder" data-testid={`option-drag-${name}-${item.key}`}>
        <GripVertical className="h-4 w-4" />
      </button>
      {editing ? (
        <>
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-7 w-9 cursor-pointer rounded border border-border bg-transparent" data-testid={`option-color-${item.key}`} />
          <Input value={label} onChange={(e) => setLabel(e.target.value)} className="h-8 flex-1" data-testid={`option-label-${item.key}`} />
          <Button size="sm" onClick={save} disabled={busy} data-testid={`option-save-${item.key}`}>{busy && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}Save</Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
        </>
      ) : (
        <>
          <span className="h-4 w-4 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
          <span className="flex-1 text-sm">{item.label} <span className="text-xs text-muted-foreground">· {item.key}</span></span>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(true)} data-testid={`option-edit-${item.key}`}><Pencil className="h-3.5 w-3.5" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={remove} data-testid={`option-delete-${item.key}`}><Trash2 className="h-3.5 w-3.5" /></Button>
        </>
      )}
    </Reorder.Item>
  );
}

function TaxonomyCard({ name, label, items, onSaved }) {
  const [newLabel, setNewLabel] = useState("");
  const [newColor, setNewColor] = useState("#0D9488");
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState(items || []);
  useEffect(() => { setList(items || []); }, [items]);

  const add = async () => {
    if (!newLabel.trim()) return toast.error("Enter a label");
    setBusy(true);
    try { await api.post(`/superadmin/options/${name}`, { label: newLabel, color: newColor }); toast.success("Option added"); setNewLabel(""); onSaved(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const persistOrder = async () => {
    const keys = list.map((i) => i.key);
    if (keys.join("|") === (items || []).map((i) => i.key).join("|")) return;
    try { await api.post(`/superadmin/options/${name}/reorder`, { keys }); toast.success("Order saved"); onSaved(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setList(items || []); }
  };

  return (
    <Card className="space-y-3 p-4" data-testid={`taxonomy-${name}`}>
      <p className="font-heading font-semibold">{label}</p>
      <Reorder.Group axis="y" values={list} onReorder={setList} className="space-y-1.5">
        {list.map((it) => <OptionRow key={it.key} name={name} item={it} onSaved={onSaved} onPersistOrder={persistOrder} />)}
      </Reorder.Group>
      <div className="flex items-center gap-2 border-t border-border pt-3">
        <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)} className="h-8 w-9 cursor-pointer rounded border border-border bg-transparent" data-testid={`taxonomy-newcolor-${name}`} />
        <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="New option label" className="h-8 flex-1" data-testid={`taxonomy-newlabel-${name}`} />
        <Button size="sm" onClick={add} disabled={busy} data-testid={`taxonomy-add-${name}`}><Plus className="mr-1 h-3.5 w-3.5" /> Add</Button>
      </div>
    </Card>
  );
}

const ROLE_ITEMS = (
  <>
    <SelectItem value="agent">Agent</SelectItem>
    <SelectItem value="manager">Manager</SelectItem>
    <SelectItem value="admin">Admin</SelectItem>
  </>
);

function UserDialog({ orgId, member, onClose, onDone }) {
  const editingUser = !!member;
  const [form, setForm] = useState({
    name: member?.name || "", email: member?.email || "", password: "", role: member?.role || "agent",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!editingUser && (!form.email.trim() || form.password.length < 6)) return toast.error("Email and a 6+ char password are required");
    setBusy(true);
    try {
      if (editingUser) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.patch(`/superadmin/users/${member.user_id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/superadmin/users", { org_id: orgId, name: form.name, email: form.email, password: form.password, role: form.role });
        toast.success("User created");
      }
      onDone(); onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="sa-user-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editingUser ? "Edit user" : "Add user"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={form.name} onChange={set("name")} className="mt-1.5" data-testid="sa-user-name" /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} disabled={editingUser} className="mt-1.5" data-testid="sa-user-email" /></div>
          <div><Label>{editingUser ? "Reset password (optional)" : "Password"}</Label><Input value={form.password} onChange={set("password")} className="mt-1.5" placeholder="Min. 6 characters" data-testid="sa-user-password" /></div>
          <div><Label>Role</Label>
            <Select value={form.role} onValueChange={set("role")}>
              <SelectTrigger className="mt-1.5" data-testid="sa-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLE_ITEMS}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy} data-testid="sa-user-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editingUser ? "Save" : "Create user"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const prettyType = (t) => (t || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const fmtWhen = (iso) => { try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };

const LeadRows = ({ items, empty }) => (
  <div className="max-h-[46vh] space-y-2 overflow-y-auto">
    {(!items || items.length === 0) && <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{empty}</p>}
    {(items || []).map((l) => (
      <div key={l.lead_id} className="flex items-center justify-between gap-3 rounded-lg border border-border p-3" data-testid={`sa-newlead-row-${l.lead_id}`}>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{l.name}</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
            <span className="font-medium text-foreground/70">{l.org_name}</span>
            {l.type && <><span>·</span><span>{prettyType(l.type)}</span></>}
            <span>·</span><span>{fmtWhen(l.created_at)}</span>
          </p>
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${l.assigned ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600"}`}>
          {l.assigned ? "Assigned" : "Unassigned"}
        </span>
      </div>
    ))}
  </div>
);

function SANewLeadsButton({ agencies = [] }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("new");
  const [assignOrg, setAssignOrg] = useState("");
  const [assignMode, setAssignMode] = useState("roundrobin");
  const [assigning, setAssigning] = useState(false);
  const load = () => api.get("/superadmin/new-leads").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);
  const openDialog = async () => {
    setOpen(true);
    try { await api.post("/superadmin/new-leads/seen"); } catch { /* ignore */ }
    setData((d) => (d ? { ...d, unseen_count: 0 } : d));
  };
  const goDistribute = () => { setOpen(false); setTimeout(() => document.getElementById("sa-distribute-panel")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150); };
  const doAssign = async () => {
    if (!assignOrg) return toast.error("Pick an agency to receive the leads");
    setAssigning(true);
    try {
      const { data: r } = await api.post("/superadmin/assign-unassigned", { org_id: assignOrg, mode: assignMode });
      toast.success(`Assigned ${r.assigned} lead${r.assigned !== 1 ? "s" : ""} across ${r.agents} agent${r.agents !== 1 ? "s" : ""}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAssigning(false); }
  };
  const unseen = data?.unseen_count || 0;
  const new24 = data?.new_24h?.count || 0;
  const unassigned = data?.unassigned || 0;
  const distributed = data?.distributed?.count || 0;

  const TabBtn = ({ id, label, count, icon: Icon }) => (
    <button onClick={() => setTab(id)} data-testid={`sa-newleads-tab-${id}`}
      className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${tab === id ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
      <Icon className="h-3.5 w-3.5" /> {label} <span className="rounded-full bg-background px-1.5 py-0.5 font-mono">{count}</span>
    </button>
  );

  return (
    <>
      {unseen > 0 ? (
        <button onClick={openDialog} data-testid="sa-new-leads-button"
          className="inline-flex animate-pulse items-center gap-2 rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-lg ring-2 ring-red-400/60 transition-transform hover:scale-105">
          <BellRing className="h-4 w-4" /> New Leads ({unseen})
        </button>
      ) : (
        <button onClick={openDialog} data-testid="sa-new-leads-button"
          className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
          <Bell className="h-4 w-4" /> New Leads
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="sa-new-leads-dialog">
          <DialogHeader><DialogTitle className="font-heading">New leads across the platform</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <TabBtn id="new" label="New (24h)" count={new24} icon={BellRing} />
            <TabBtn id="unassigned" label="Unassigned" count={unassigned} icon={Inbox} />
            <TabBtn id="distributed" label="Distributed" count={distributed} icon={Send} />
          </div>
          <div className="mt-3">
            {tab === "new" && <LeadRows items={data?.new_24h?.items} empty="No leads created in the last 24 hours." />}
            {tab === "unassigned" && (
              <div className="space-y-3">
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
                  <p className="text-2xl font-bold">{unassigned}</p>
                  <p className="text-xs text-muted-foreground">leads not yet assigned to any agent, platform-wide</p>
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                    <Select value={assignOrg} onValueChange={setAssignOrg}>
                      <SelectTrigger className="flex-1" data-testid="sa-assign-agency-select"><SelectValue placeholder="Pick an agency…" /></SelectTrigger>
                      <SelectContent>
                        {agencies.map((o) => <SelectItem key={o.org_id} value={o.org_id}>{o.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={assignMode} onValueChange={setAssignMode}>
                      <SelectTrigger className="sm:w-44" data-testid="sa-assign-mode-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="roundrobin">Round-robin</SelectItem>
                        <SelectItem value="by_type">By insurance type</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={doAssign} disabled={assigning || !assignOrg} data-testid="sa-assign-unassigned-button">
                      {assigning ? <Loader2 className="h-4 w-4 animate-spin" /> : "Assign that agency's leads"}
                    </Button>
                  </div>
                  <button onClick={goDistribute} data-testid="sa-newleads-go-distribute" className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                    Or upload &amp; distribute brand-new leads <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
                <LeadRows items={(data?.new_24h?.items || []).filter((l) => !l.assigned)} empty="No unassigned leads created in the last 24 hours." />
              </div>
            )}
            {tab === "distributed" && <LeadRows items={data?.distributed?.items} empty="You haven't distributed any leads in the last 7 days." />}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function SuperAdmin() {
  const { options, reload: reloadOptions } = useOptions();
  const [a, setA] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [csv, setCsv] = useState("");
  const [distFile, setDistFile] = useState("");
  const [orgId, setOrgId] = useState("");
  const [distMode, setDistMode] = useState("roundrobin");
  const [usersByOrg, setUsersByOrg] = useState({});
  const [busy, setBusy] = useState(false);
  const [userDialog, setUserDialog] = useState(null); // { orgId, member? }
  const [masters, setMasters] = useState([]);
  const [masterName, setMasterName] = useState("");
  const [invite, setInvite] = useState(null);
  const [analytics, setAnalytics] = useState({});
  const submitInvite = async () => {
    if (!invite.name.trim() || !invite.email.trim() || invite.password.length < 6) return toast.error("Name, email and a 6+ char password required");
    try { await api.post(`/superadmin/masters/${invite.mid}/invite-individual`, { name: invite.name, email: invite.email, password: invite.password, company_name: invite.company_name || null }); toast.success("Individual added to master"); setInvite(null); loadMasters(); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const loadAnalytics = async (mid) => {
    if (analytics[mid]) { setAnalytics((p) => { const n = { ...p }; delete n[mid]; return n; }); return; }
    try { const { data } = await api.get(`/superadmin/masters/${mid}/analytics`); setAnalytics((p) => ({ ...p, [mid]: data })); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const loadMasters = () => api.get("/superadmin/masters").then(({ data }) => setMasters(data)).catch(() => {});
  const createMaster = async () => {
    if (!masterName.trim()) return toast.error("Name the master account");
    try { await api.post("/superadmin/masters", { name: masterName }); setMasterName(""); toast.success("Master account created"); loadMasters(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteMaster = async (m) => {
    if (!window.confirm(`Delete "${m.name}"? Member companies keep their leads.`)) return;
    try { await api.delete(`/superadmin/masters/${m.master_id}`); toast.success("Deleted"); loadMasters(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const addMasterMember = async (mid, org_id) => {
    if (!org_id) return;
    try { await api.post(`/superadmin/masters/${mid}/members`, { org_id }); toast.success("Company added"); loadMasters(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const removeMasterMember = async (mid, org_id) => {
    try { await api.delete(`/superadmin/masters/${mid}/members/${org_id}`); toast.success("Company removed"); loadMasters(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const loadUsers = async (org_id) => {
    try { const { data } = await api.get(`/superadmin/users?org_id=${org_id}`); setUsersByOrg((p) => ({ ...p, [org_id]: data })); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleUsers = async (org_id) => {
    if (usersByOrg[org_id]) { setUsersByOrg((p) => { const n = { ...p }; delete n[org_id]; return n; }); return; }
    await loadUsers(org_id);
  };

  const load = useCallback(() => {
    api.get("/superadmin/analytics").then(({ data }) => setA(data)).catch(() => {});
    api.get("/superadmin/agencies").then(({ data }) => setOrgs(data)).catch(() => {});
    loadMasters();
  }, []);
  useEffect(() => { load(); }, [load]);

  const impersonate = async (uid) => {
    if (!uid) return;
    try { await api.post(`/superadmin/impersonate/${uid}`); toast.success("Impersonation started"); window.location.href = "/app"; }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.name}? Their leads will be unassigned.`)) return;
    try { await api.delete(`/superadmin/users/${u.user_id}`); toast.success("User deleted"); loadUsers(u.org_id); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const [selOrgs, setSelOrgs] = useState(new Set());
  const [selUsers, setSelUsers] = useState({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const toggleOrgSel = (id) => setSelOrgs((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleUserSel = (orgId, uid) => setSelUsers((p) => { const set = new Set(p[orgId] || []); set.has(uid) ? set.delete(uid) : set.add(uid); return { ...p, [orgId]: set }; });
  const deleteAgency = async (o) => {
    if (!window.confirm(`Delete "${o.name}"? This permanently removes its users, leads and data.`)) return;
    try { await api.delete(`/superadmin/agencies/${o.org_id}`); toast.success("Agency deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const bulkDeleteAgencies = async () => {
    const ids = [...selOrgs];
    if (!ids.length) return;
    let ok = 0;
    for (const id of ids) { try { await api.delete(`/superadmin/agencies/${id}`); ok += 1; } catch { /* skip */ } }
    toast.success(`Deleted ${ok} agenc${ok !== 1 ? "ies" : "y"}`);
    setSelOrgs(new Set()); setBulkOpen(false); setConfirmText(""); load();
  };
  const bulkDeleteUsers = async (orgId) => {
    const ids = [...(selUsers[orgId] || [])];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} user${ids.length > 1 ? "s" : ""}? Their leads will be unassigned.`)) return;
    let ok = 0;
    for (const id of ids) { try { await api.delete(`/superadmin/users/${id}`); ok += 1; } catch { /* skip */ } }
    toast.success(`Deleted ${ok} user${ok !== 1 ? "s" : ""}`); setSelUsers((p) => ({ ...p, [orgId]: new Set() })); loadUsers(orgId); load();
  };

  const distribute = async () => {
    const leads = parseCSV(csv);
    if (!orgId) return toast.error("Pick an agency");
    if (!leads.length) return toast.error("Upload a CSV with a header row and at least one lead");
    setBusy(true);
    try {
      const { data } = await api.post("/superadmin/distribute-leads", { org_id: orgId, mode: distMode, leads });
      toast.success(`Imported ${data.imported} leads across ${data.distributed_to} agent(s)`);
      setCsv(""); setDistFile(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const onDistFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDistFile(file.name);
    const name = file.name.toLowerCase();
    const reader = new FileReader();
    if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
      reader.onload = (ev) => {
        try {
          const wb = XLSX.read(ev.target.result, { type: "array" });
          const ws = wb.Sheets[wb.SheetNames[0]];
          setCsv(XLSX.utils.sheet_to_csv(ws));
        } catch { toast.error("Couldn't read that Excel file — check it has a header row"); }
      };
      reader.readAsArrayBuffer(file);
    } else {
      reader.onload = (ev) => setCsv(ev.target.result);
      reader.readAsText(file);
    }
  };
  const toggleAgencyFree = async (o) => {
    let reason = "";
    if (!o.free_account) {
      reason = window.prompt("Add a short audit note for this free account (e.g. 'partner comp'):", "") || "";
    }
    try {
      await api.patch(`/superadmin/agencies/${o.org_id}/free`, { free: !o.free_account, reason });
      toast.success(o.free_account ? "Free access removed" : "Marked as a free account");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleUserFree = async (orgId, u) => {
    let reason = "";
    if (!u.free_account) {
      reason = window.prompt("Add a short audit note for this free user (e.g. 'comped seat'):", "") || "";
    }
    try {
      await api.patch(`/superadmin/users/${u.user_id}/free`, { free: !u.free_account, reason });
      toast.success(u.free_account ? "Free flag removed" : "User marked as free (no billed seat)");
      loadUsers(orgId);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="superadmin-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold sm:text-3xl">Platform Console</h1>
          <p className="mt-1 text-sm text-muted-foreground">Cross-agency analytics, users, taxonomies, impersonation, and lead distribution.</p>
        </div>
        <SANewLeadsButton agencies={orgs} />
      </div>

      {a && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat icon={Building2} label="Agencies" value={a.orgs} />
          <Stat icon={Users2} label="Users" value={`${a.active_users}/${a.users}`} />
          <Stat icon={ListChecks} label="Leads" value={a.leads_total} />
          <Stat icon={DollarSign} label="MRR" value={`$${a.subscriptions.mrr.toFixed(2)}`} />
        </div>
      )}
      {a && (
        <Card className="p-4 text-sm" data-testid="subs-summary">
          <p className="font-medium">Subscriptions</p>
          <p className="mt-1 text-muted-foreground">
            {a.subscriptions.active} active · {a.subscriptions.trialing} on trial · {a.subscriptions.trials_expiring} trial(s) expiring within 7 days · plans: {Object.entries(a.subscriptions.plans_in_use).map(([k, v]) => `${k} ${v}`).join(", ") || "none"}
            <span className="mt-1 block" data-testid="free-accounts-stat"><Gift className="mr-1 inline h-3 w-3 text-emerald-600 dark:text-emerald-400" />{a.subscriptions.free_agencies || 0} free agenc{(a.subscriptions.free_agencies || 0) === 1 ? "y" : "ies"} · {a.subscriptions.free_users || 0} free user(s) · ~${(a.subscriptions.mrr_forgone || 0).toLocaleString()}/mo forgone</span>
          </p>
        </Card>
      )}

      <div>
        <div className="mb-2 flex items-center gap-2"><Tags className="h-4 w-4 text-primary" /><h2 className="font-heading text-lg font-semibold">Taxonomies</h2></div>
        <p className="mb-3 text-xs text-muted-foreground">Add, edit or delete the dropdown options used across every agency. Changes apply after users reload.</p>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          {TAXONOMY_META.map((t) => (
            <TaxonomyCard key={t.name} name={t.name} label={t.label} items={options?.[t.name]} onSaved={reloadOptions} />
          ))}
        </div>
      </div>

      <Card className="space-y-3 p-5" data-testid="masters-panel">
        <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /><p className="font-heading font-semibold">Master accounts</p></div>
        <p className="text-xs text-muted-foreground">Group companies and individual users under one master account. Members can share leads into a common master pool (owners choose which leads on demand).</p>
        <div className="flex flex-wrap items-center gap-2">
          <Input value={masterName} onChange={(e) => setMasterName(e.target.value)} placeholder="New master account name" className="max-w-xs" data-testid="master-name-input" />
          <Button onClick={createMaster} data-testid="create-master-button"><Plus className="mr-1 h-3.5 w-3.5" /> Create</Button>
        </div>
        <div className="space-y-2">
          {masters.length === 0 && <p className="text-sm text-muted-foreground">No master accounts yet.</p>}
          {masters.map((m) => {
            const memberIds = (m.members || []).map((x) => x.org_id);
            const avail = orgs.filter((o) => !memberIds.includes(o.org_id));
            return (
              <div key={m.master_id} className="rounded-lg border border-border p-3" data-testid={`master-${m.master_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium">{m.name} <span className="text-xs text-muted-foreground">· {(m.members || []).length} member(s)</span></p>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteMaster(m)} data-testid={`delete-master-${m.master_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(m.members || []).map((mem) => (
                    <span key={mem.org_id} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs" data-testid={`master-member-${mem.org_id}`}>
                      {mem.name}
                      <button type="button" onClick={() => removeMasterMember(m.master_id, mem.org_id)} className="text-muted-foreground hover:text-destructive" data-testid={`remove-member-${mem.org_id}`}>×</button>
                    </span>
                  ))}
                  {(m.members || []).length === 0 && <span className="text-xs text-muted-foreground">No companies yet.</span>}
                </div>
                <div className="mt-2">
                  <Select value="" onValueChange={(v) => addMasterMember(m.master_id, v)}>
                    <SelectTrigger className="h-8 w-64" data-testid={`add-member-select-${m.master_id}`}><SelectValue placeholder="Add a company / individual…" /></SelectTrigger>
                    <SelectContent>{avail.map((o) => <SelectItem key={o.org_id} value={o.org_id}>{o.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => setInvite(invite?.mid === m.master_id ? null : { mid: m.master_id, name: "", email: "", password: "", company_name: "" })} data-testid={`invite-individual-${m.master_id}`}><UserPlus className="mr-1 h-3.5 w-3.5" /> Invite individual</Button>
                  <Button size="sm" variant="outline" onClick={() => loadAnalytics(m.master_id)} data-testid={`master-analytics-${m.master_id}`}><ListChecks className="mr-1 h-3.5 w-3.5" /> {analytics[m.master_id] ? "Hide analytics" : "Analytics"}</Button>
                </div>
                {invite?.mid === m.master_id && (
                  <div className="mt-2 grid grid-cols-1 gap-2 rounded-md border border-border p-2 sm:grid-cols-2" data-testid={`invite-form-${m.master_id}`}>
                    <Input placeholder="Full name" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} data-testid="invite-name" />
                    <Input placeholder="Email" type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} data-testid="invite-email" />
                    <Input placeholder="Company name (optional)" value={invite.company_name} onChange={(e) => setInvite({ ...invite, company_name: e.target.value })} data-testid="invite-company" />
                    <Input placeholder="Temp password (min 6)" value={invite.password} onChange={(e) => setInvite({ ...invite, password: e.target.value })} data-testid="invite-password" />
                    <Button size="sm" className="sm:col-span-2" onClick={submitInvite} data-testid="invite-submit">Create & add to master</Button>
                  </div>
                )}
                {analytics[m.master_id] && (
                  <div className="mt-2 rounded-md border border-border p-2 text-xs" data-testid={`analytics-${m.master_id}`}>
                    <p className="font-medium">Totals: {analytics[m.master_id].totals.users} agents · {analytics[m.master_id].totals.leads} leads · {analytics[m.master_id].totals.closed_won} closed-won</p>
                    <div className="mt-1 space-y-0.5">
                      {analytics[m.master_id].members.map((r) => (
                        <div key={r.org_id} className="flex justify-between gap-2"><span className="truncate">{r.name}</span><span className="shrink-0 text-muted-foreground">{r.users}a · {r.leads}l · {r.closed_won}w · {r.pooled} pooled</span></div>
                      ))}
                      {analytics[m.master_id].members.length === 0 && <span className="text-muted-foreground">No member companies yet.</span>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="overflow-hidden" data-testid="agencies-table">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <span className="font-heading font-semibold">Agencies &amp; users</span>
          {selOrgs.size > 0 && (
            <Button size="sm" variant="destructive" onClick={() => { setConfirmText(""); setBulkOpen(true); }} data-testid="bulk-delete-agencies">
              <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete {selOrgs.size} selected
            </Button>
          )}
        </div>
        <div className="divide-y divide-border/60">
          {orgs.map((o) => (
            <div key={o.org_id} className="px-4 py-3 text-sm" data-testid={`agency-row-${o.org_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex min-w-0 items-start gap-3">
                  <Checkbox className="mt-1" checked={selOrgs.has(o.org_id)} onCheckedChange={() => toggleOrgSel(o.org_id)} data-testid={`select-agency-${o.org_id}`} />
                  <div className="min-w-0">
                    <p className="font-medium">{o.name}{o.free_account && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400" data-testid={`free-badge-agency-${o.org_id}`}><Gift className="h-2.5 w-2.5" /> Free</span>}</p>
                    <p className="text-xs text-muted-foreground">{o.users} users · {o.leads} leads · {o.free_account ? `Free account${o.free_reason ? ` — ${o.free_reason}` : ""}` : `${o.plan || "trial"} (${o.status || "trialing"})`} · {o.owner?.email || "—"}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => toggleAgencyFree(o)} data-testid={`toggle-free-agency-${o.org_id}`} className={o.free_account ? "border-emerald-500/50 text-emerald-600 dark:text-emerald-400" : ""}><Gift className="mr-1 h-3.5 w-3.5" /> {o.free_account ? "Free ✓" : "Make free"}</Button>
                  <Button size="sm" variant="ghost" onClick={() => toggleUsers(o.org_id)} data-testid={`toggle-users-${o.org_id}`}>{usersByOrg[o.org_id] ? "Hide users" : "Users"}</Button>
                  <Button size="sm" variant="outline" onClick={() => setUserDialog({ orgId: o.org_id })} data-testid={`add-user-${o.org_id}`}><UserPlus className="mr-1 h-3.5 w-3.5" /> Add user</Button>
                  {o.owner_id && <Button size="sm" variant="outline" onClick={() => impersonate(o.owner_id)} data-testid={`impersonate-owner-${o.org_id}`}>Impersonate owner</Button>}
                  <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => deleteAgency(o)} data-testid={`delete-agency-${o.org_id}`}><Trash2 className="mr-1 h-3.5 w-3.5" /> Delete</Button>
                </div>
              </div>
              {usersByOrg[o.org_id] && (
                <div className="mt-2 space-y-1 border-t border-border/60 pt-2" data-testid={`users-list-${o.org_id}`}>
                  {selUsers[o.org_id]?.size > 0 && (
                    <div className="flex justify-end pb-1">
                      <Button size="sm" variant="destructive" onClick={() => bulkDeleteUsers(o.org_id)} data-testid={`bulk-delete-users-${o.org_id}`}><Trash2 className="mr-1 h-3.5 w-3.5" /> Delete {selUsers[o.org_id].size} selected</Button>
                    </div>
                  )}
                  {usersByOrg[o.org_id].map((u) => (
                    <div key={u.user_id} className="flex flex-wrap items-center justify-between gap-2" data-testid={`sa-user-${u.user_id}`}>
                      <span className="flex min-w-0 items-center gap-2 truncate">
                        <Checkbox checked={!!selUsers[o.org_id]?.has(u.user_id)} onCheckedChange={() => toggleUserSel(o.org_id, u.user_id)} data-testid={`select-user-${u.user_id}`} />
                        <span className="truncate">{u.name} <span className="text-xs text-muted-foreground">· {u.role} · {u.email} {u.free_account && "· free"} {u.active === false && "· inactive"}</span></span>
                      </span>
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="ghost" onClick={() => toggleUserFree(o.org_id, u)} data-testid={`toggle-free-user-${u.user_id}`} className={u.free_account ? "text-emerald-600 dark:text-emerald-400" : ""} title={u.free_account ? "Remove free flag" : "Mark as free (no billed seat)"}><Gift className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => setUserDialog({ orgId: o.org_id, member: u })} data-testid={`edit-user-${u.user_id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteUser(u)} data-testid={`delete-user-${u.user_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="outline" onClick={() => impersonate(u.user_id)} data-testid={`impersonate-user-${u.user_id}`}>Impersonate</Button>
                      </div>
                    </div>
                  ))}
                  {usersByOrg[o.org_id].length === 0 && <p className="text-xs text-muted-foreground">No users.</p>}
                </div>
              )}
            </div>
          ))}
          {orgs.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No agencies yet.</p>}
        </div>
      </Card>

      <Card className="space-y-3 p-5" data-testid="distribute-panel" id="sa-distribute-panel">
        <p className="font-heading font-semibold">Upload &amp; distribute leads</p>
        <p className="text-xs text-muted-foreground">Upload a <span className="font-medium text-foreground">CSV or Excel</span> file with a header row: name,phone,email,company,city,state,zip,insurance_type,premium_estimate. Choose how leads are assigned: <span className="font-medium text-foreground">by agency</span> (round-robin across all agents) or <span className="font-medium text-foreground">by insurance type</span> (routed to agents who handle that coverage).</p>
        <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border py-6 text-sm text-muted-foreground transition-colors hover:border-primary/50" data-testid="distribute-dropzone">
          <Upload className="h-4 w-4" /> {distFile ? `${distFile} · ${parseCSV(csv).length} lead(s) parsed` : "Click to upload a .csv or .xlsx file"}
          <input type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="distribute-file-input" onChange={onDistFile} />
        </label>
        {csv && parseCSV(csv).length > 0 && (
          <div className="max-h-48 overflow-y-auto rounded-lg border border-border" data-testid="distribute-preview">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                <tr className="text-left text-xs uppercase text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">Phone</th>
                  <th className="px-3 py-2 font-medium">City / State</th>
                  <th className="px-3 py-2 font-medium">Type</th>
                </tr>
              </thead>
              <tbody>
                {parseCSV(csv).slice(0, 100).map((r, i) => (
                  <tr key={i} className="border-t border-border/60" data-testid={`distribute-preview-row-${i}`}>
                    <td className="px-3 py-2 font-medium text-foreground">{r.name || <span className="italic opacity-60">—</span>}</td>
                    <td className="px-3 py-2 text-muted-foreground">{r.phone || "—"}</td>
                    <td className="px-3 py-2 text-muted-foreground">{[r.city, r.state].filter(Boolean).join(", ") || "—"}</td>
                    <td className="px-3 py-2 text-muted-foreground">{r.insurance_type || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Select value={orgId} onValueChange={setOrgId}>
            <SelectTrigger className="w-56" data-testid="distribute-agency"><SelectValue placeholder="Choose agency" /></SelectTrigger>
            <SelectContent>{orgs.map((o) => <SelectItem key={o.org_id} value={o.org_id}>{o.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={distMode} onValueChange={setDistMode}>
            <SelectTrigger className="w-56" data-testid="distribute-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="roundrobin">By agency (round-robin)</SelectItem>
              <SelectItem value="by_type">By insurance type</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={distribute} disabled={busy} data-testid="distribute-button">Distribute leads</Button>
        </div>
      </Card>

      <Dialog open={bulkOpen} onOpenChange={(o) => { setBulkOpen(o); if (!o) setConfirmText(""); }}>
        <DialogContent className="sm:max-w-md" data-testid="bulk-delete-dialog">
          <DialogHeader><DialogTitle className="font-heading text-destructive">Delete {selOrgs.size} agenc{selOrgs.size !== 1 ? "ies" : "y"}?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This permanently removes every selected agency along with its users, leads and data. This cannot be undone. Type <span className="font-mono font-semibold text-foreground">DELETE</span> to confirm.</p>
          <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" data-testid="bulk-delete-confirm-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} data-testid="bulk-delete-cancel">Cancel</Button>
            <Button variant="destructive" disabled={confirmText !== "DELETE"} onClick={bulkDeleteAgencies} data-testid="bulk-delete-confirm">Delete permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {userDialog && (
        <UserDialog orgId={userDialog.orgId} member={userDialog.member} onClose={() => setUserDialog(null)}
          onDone={() => { loadUsers(userDialog.orgId); load(); }} />
      )}
    </div>
  );
}
